<template>
  <header class="redesign-header">
    <div class="redesign-header__left">
      <nav class="rd-breadcrumb" aria-label="경로">
        <template v-if="breadcrumb.group">
          <span class="rd-breadcrumb__item">{{ breadcrumb.group }}</span>
          <span class="rd-breadcrumb__sep">&rsaquo;</span>
        </template>
        <span class="rd-breadcrumb__item rd-breadcrumb__item--current">{{ breadcrumb.title }}</span>
      </nav>
    </div>
    <div class="redesign-header__right">
      <button v-if="canQuickReception" class="redesign-header__cta redesign-header__cta--reception" @click="goToReception">
        <i class="fa-solid fa-plus"></i> 빠른 접수
      </button>
      <button v-if="canQuickConsultation" class="redesign-header__cta redesign-header__cta--as" @click="goToConsultation">
        <i class="fa-solid fa-bolt"></i> 빠른 상담
      </button>
      <div ref="profile" class="redesign-header__profile">
        <button
          type="button"
          class="redesign-header__profile-trigger"
          aria-haspopup="true"
          :aria-expanded="isProfileMenuOpen"
          @click.stop="toggleProfileMenu"
        >
          <div class="redesign-header__avatar">
            {{ userInitial }}
          </div>
          <span class="redesign-header__username">{{ userName }}</span>
        </button>
        <div
          class="redesign-header__profile-menu"
          :class="{ 'redesign-header__profile-menu--open': isProfileMenuOpen }"
        >
          <a href="#myPage" data-bs-toggle="modal" class="redesign-header__profile-item" @click="closeProfileMenu">
            {{ $t('login.modifyingInformation') }}
          </a>
          <button class="redesign-header__profile-item" @click="logout()">
            {{ $t('login.logout') }}
          </button>
        </div>
      </div>
    </div>
    <MyPage/>
  </header>
</template>
<script>
import MyPage from '@/components/mypage/MyPage.vue'
import { mapGetters } from 'vuex'
import { mapState as mapPiniaState } from 'pinia'
import { useTabStore } from '@/stores/tabs'
import { useMenuStore } from '@/stores/menu'
import { logout } from '@/api/domain/common'
import { useAssistant } from '@/composables/useAssistant'

/**
 * @component AppHeader
 * @description PC 전용 상단 헤더 컴포넌트 (1280px 미만에서는 hidden).
 * 좌측 브레드크럼({group} > {title}), 빠른 접수/상담 버튼, 사용자 프로필 메뉴를 포함합니다.
 * 브레드크럼은 tabStore.activeTab 의 title + menuStore.tree 에서 소속 그룹을 역참조하여 생성합니다.
 */
export default {
  // ── 컴포넌트 등록 ──
  components: {
    MyPage
  },
  setup() {
    const menuStore = useMenuStore()
    return { menuStore }
  },
  // ── 계산된 속성 ──
  computed: {
    ...mapGetters(['userName']),
    ...mapPiniaState(useTabStore, ['activeTab', 'activeTabTitle', 'activeTabPath']),
    /**
     * 사용자 이름의 첫 글자(대문자)를 반환합니다. 이름이 없으면 'U'를 반환합니다.
     */
    userInitial() {
      return this.userName ? this.userName.charAt(0).toUpperCase() : 'U'
    },
    /**
     * 현재 라우트의 meta.title 또는 name을 브레드크럼 텍스트로 반환합니다.
     */
    currentRouteName() {
      return this.$route?.meta?.title || this.$route?.name || ''
    },
    /**
     * 현재 활성 탭 기준 브레드크럼 정보를 반환합니다.
     * group 은 menuStore.tree 에서 activeTabPath 로 역참조한 상위 메뉴 이름.
     * menuStore가 아직 로드되지 않았으면 title만 채워 flash 렌더를 방지합니다.
     * 그룹명이 제목과 같으면(직행 그룹 등) 그룹을 생략해 같은 이름이 두 번 나오지 않게 합니다 (SURINP-688).
     * @returns {{group: string, title: string}}
     */
    breadcrumb() {
      const title = this.activeTabTitle || this.$route?.meta?.title || this.$route?.name || ''
      if (!this.menuStore.loaded) return { group: '', title }
      const group = this.menuStore.findGroupByPath(this.activeTabPath)
      const groupName = group ? group.name : ''
      return { group: groupName === title ? '' : groupName, title }
    },
    /**
     * 「빠른 접수」 표시 여부 — 접수등록 메뉴 권한이 있을 때만 (라우터 menuGuard 와 같은 판정, SURINP-692).
     */
    canQuickReception() {
      return this.menuStore.canAccessPath('/repair-request-registration')
    },
    /**
     * 「빠른 상담」 표시 여부 — 상담등록 메뉴 권한이 있을 때만 (SURINP-692).
     */
    canQuickConsultation() {
      return this.menuStore.canAccessPath('/consultation-reception')
    }
  },
  // ── 반응형 데이터 ──
  data() {
    return {
      popupTitle: '개인 정보 수정',
      isProfileMenuOpen: false
    }
  },
  // ── 생명주기 훅 ──
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscKey)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscKey)
  },
  // ── 메서드 ──
  methods: {
    /**
     * 사용자 프로필 메뉴(정보 수정/로그아웃)의 열림 상태를 토글합니다.
     * 트리거에 @click.stop 이 걸려 있어 document 바깥 클릭 리스너로 즉시 닫히지 않습니다.
     */
    toggleProfileMenu() {
      this.isProfileMenuOpen = !this.isProfileMenuOpen
    },
    /**
     * 사용자 프로필 메뉴를 닫습니다.
     */
    closeProfileMenu() {
      this.isProfileMenuOpen = false
    },
    /**
     * 프로필 영역 바깥을 클릭하면 메뉴를 닫습니다.
     * $refs.profile 기준 contains() 판정으로 직접 DOM 조작을 피합니다.
     * @param {MouseEvent} e
     */
    handleOutsideClick(e) {
      if (!this.isProfileMenuOpen) return
      const el = this.$refs.profile
      if (el && !el.contains(e.target)) {
        this.closeProfileMenu()
      }
    },
    /**
     * 메뉴가 열린 상태에서 Escape 키를 누르면 닫습니다(드롭다운 키보드 접근성).
     * @param {KeyboardEvent} e
     */
    handleEscKey(e) {
      if (this.isProfileMenuOpen && e.key === 'Escape') {
        this.closeProfileMenu()
      }
    },
    /**
     * 빠른 접수 버튼 클릭 시 수리 요청 등록 페이지로 이동합니다.
     */
    goToReception() {
      this.$router.push('/repair-request-registration')
    },
    /**
     * 빠른 상담 버튼 클릭 시 상담 등록 페이지로 이동합니다.
     */
    goToConsultation() {
      this.$router.push('/consultation-reception')
    },
    /**
     * 로그아웃 API를 호출하고 세션을 초기화한 후 로그인 페이지로 이동합니다.
     */
    async logout() {
      const vm = this
      this.closeProfileMenu()

      await logout('post')
        .then(response => {
          // 어시스턴트 대화 초기화 — 다음 로그인 계정에 이전 계정 이력 노출 방지.
          // cancelSend가 진행 중 스트리밍을 중단(BE 취소 신호는 스트림 시작 시점
          // 토큰으로 발신)하고, reset이 이력·플래그를 비운다.
          const assistant = useAssistant()
          assistant.cancelSend()
          this.$store.commit('user', {})
          sessionStorage.removeItem('authToken')
          // 메뉴 트리·작업 탭 초기화 — 다음 로그인 계정에 이전 계정 메뉴·탭 노출 방지 (SURINP-831)
          this.menuStore.clearAll()
          useTabStore().clearAll()
          assistant.store.reset()
          window.appAlert(this.$t('message.logout'))
          vm.$router.push({ path: '/login' })
        })
        .catch(error => { window.__APP_LOG__(error) })
    }
  }
}
</script>
<style scoped>
/* --- 헤더 컨테이너 (높이 64px 고정, 1279px 이하 hidden) --- */
.redesign-header {
  position: relative;
  height: var(--header-height, 64px);
  background: #FFFFFF;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  z-index: var(--z-header, 1000);
}

/* --- 좌측: 브레드크럼 영역 --- */
.redesign-header__left {
  display: flex;
  align-items: center;
}

.redesign-header__breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
}

.redesign-header__breadcrumb-item {
  font-size: var(--font-body-md, 0.875rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--pc-text-strong, #0F172A);
}

/* --- 우측: 액션 버튼 / 검색 / 프로필 영역 --- */
.redesign-header__right {
  display: flex;
  align-items: center;
  gap: var(--space-5, 20px);
}

/* --- 빠른 접수/상담 CTA 버튼 --- */
.redesign-header__cta {
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  transition: opacity var(--transition-fast, 0.15s ease);
}
.redesign-header__cta:hover {
  opacity: 0.85;
}
.redesign-header__cta--reception {
  background: var(--color-success, #2E7D5B);
}
.redesign-header__cta--as {
  background: var(--color-info, #3670B3);
}

/* --- 사용자 프로필 (아바타 + 이름 + 드롭다운) ---
 * 래퍼는 위치 기준(position:relative)만 담당. 클릭 영역/패딩/hover 는
 * 트리거 버튼이 전담하여 시각적 영역과 실제 클릭 영역을 일치시킨다(SURINP-310).
 */
.redesign-header__profile {
  display: flex;
  align-items: center;
  position: relative;
}

.redesign-header__avatar {
  width: 34px;
  height: 34px;
  border-radius: var(--radius-full, 9999px);
  background: linear-gradient(135deg, var(--color-primary-500, #3B5998), var(--color-info, #3670B3));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-body-sm, 0.8125rem);
  font-weight: var(--font-weight-semibold, 600);
  flex-shrink: 0;
}

.redesign-header__username {
  font-size: var(--font-body-sm, 0.8125rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--pc-text, #39414E);
  max-width: 126px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --- 프로필 메뉴 트리거 (아바타 + 이름, 클릭 시 메뉴 토글) ---
 * 패딩/테두리/hover 배경을 트리거가 전담해 클릭 가능한 시각 영역 전체가 동작한다.
 */
.redesign-header__profile-trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: 4px 10px 4px 4px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s ease), border-color var(--transition-fast, 0.15s ease);
}

.redesign-header__profile-trigger:hover {
  background: var(--pc-surface-muted, #F7F9FC);
  border-color: var(--pc-border, #E3E8F1);
}

/* --- 프로필 드롭다운 메뉴 (클릭 토글 시 표시) ---
 * position: fixed로 viewport 기준 paint하여 .app-content(스크롤 컨테이너)의
 * paint layer promote에 의한 가려짐 회피. top/right는 헤더 height/padding과 정합.
 * hover 의존 제거(SURINP-310) — isProfileMenuOpen state 로 --open 클래스 토글.
 */
.redesign-header__profile-menu {
  display: none;
  position: fixed;
  top: var(--header-height, 64px);
  right: 32px;
  min-width: 160px;
  background: var(--pc-surface, #FFFFFF);
  border: 1px solid var(--pc-border, #E3E8F1);
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  z-index: var(--z-dropdown, 200);
  padding: var(--space-1, 4px) 0;
}

.redesign-header__profile-menu--open {
  display: block;
}

.redesign-header__profile-item {
  display: block;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: var(--pc-text, #39414E);
  font-size: var(--font-body-sm, 0.8125rem);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s ease);
}

.redesign-header__profile-item:hover {
  background: var(--pc-surface-muted, #F7F9FC);
}

/* --- 태블릿/모바일 반응형: 헤더 숨김 --- */
@media (max-width: 1279px) {
  .redesign-header {
    display: none;
  }
}
</style>
